import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Shield, ScrollText } from "lucide-react";

const AcceptTerms = () => {
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate("/auth");
        return;
      }
      // If already accepted, skip
      const { data: profile } = await supabase
        .from("profiles")
        .select("terms_accepted_at")
        .eq("user_id", session.user.id)
        .single();
      if (profile?.terms_accepted_at) {
        navigate("/select-plan");
        return;
      }
      setCheckingAuth(false);
    };
    check();
  }, [navigate]);

  const handleAccept = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      const { error } = await supabase
        .from("profiles")
        .update({ terms_accepted_at: new Date().toISOString() })
        .eq("user_id", session.user.id);

      if (error) throw error;
      toast.success("Termenii au fost acceptați!");
      navigate("/select-plan");
    } catch (err: any) {
      toast.error(err.message || "A apărut o eroare");
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    // Sign out and delete account essentially — just sign out
    await supabase.auth.signOut();
    toast.info("Nu poți folosi EZ Factura fără să accepți Termenii și Condițiile.");
    navigate("/");
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Se încarcă...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2">Termeni și Condiții</h1>
          <p className="text-muted-foreground text-sm">
            Înainte de a continua, te rugăm să citești și să accepți Termenii și Condițiile și Politica de Confidențialitate.
          </p>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 space-y-4 mb-6">
          <div className="flex items-start gap-3">
            <ScrollText className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>Prin acceptarea acestor termeni, confirmi că:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Ai citit și înțeles <a href="/terms" target="_blank" className="text-primary hover:underline font-medium">Termenii și Condițiile</a></li>
                <li>Ai citit și înțeles <a href="/privacy" target="_blank" className="text-primary hover:underline font-medium">Politica de Confidențialitate</a></li>
                <li>Înțelegi că EZ Factura este furnizat „ca atare" (<em>as is</em>), fără garanții de niciun fel</li>
                <li>Ești singurul responsabil pentru acuratețea datelor introduse și a facturilor emise</li>
                <li>EZ Factura nu este responsabil pentru pierderi de date, accesuri neautorizate sau orice alte daune directe sau indirecte</li>
                <li>Utilizezi platforma pe propria răspundere</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 mb-6">
          <Checkbox
            id="accept-terms"
            checked={accepted}
            onCheckedChange={(checked) => setAccepted(checked === true)}
            className="mt-0.5"
          />
          <label htmlFor="accept-terms" className="text-sm text-foreground cursor-pointer leading-snug">
            Am citit și sunt de acord cu <a href="/terms" target="_blank" className="text-primary hover:underline">Termenii și Condițiile</a> și <a href="/privacy" target="_blank" className="text-primary hover:underline">Politica de Confidențialitate</a>. Înțeleg că utilizez platforma pe propria răspundere.
          </label>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            onClick={handleAccept}
            disabled={!accepted || loading}
            className="w-full h-11 font-semibold"
          >
            {loading ? "Se procesează..." : "Accept și Continuă"}
          </Button>
          <Button
            variant="outline"
            onClick={handleDecline}
            className="w-full h-11 font-semibold text-destructive border-destructive/30 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-all duration-200 hover:scale-[1.02] active:scale-95"
          >
            Nu sunt de acord — Renunț
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AcceptTerms;
